import { useCallback, useEffect, useRef, useState } from "react";
import { formatCurrency } from "@/utils/currencyutils";

type Props = {
    value: number;
    duration?: number;
    locale?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    className?: string;
};

const AnimatedNumber = ({
    value,
    duration = 500,
    locale = "de-DE",
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    className,
}: Props) => {
    const isFirstRender = useRef(true);
    const previousValue = useRef(value);
    const animationFrame = useRef<number>(0);
    const [displayValue, setDisplayValue] = useState(value);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            setDisplayValue(value);
            previousValue.current = value;
            return;
        }
        const from = previousValue.current;
        const to = value;
        previousValue.current = value;

        if (from === to) return;

        cancelAnimationFrame(animationFrame.current);

        const startTime = performance.now();

        const animate = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = from + (to - from) * eased;

            setDisplayValue(Math.max(0, Math.min(99999, current)));

            if (progress < 1) {
                animationFrame.current = requestAnimationFrame(animate);
            } else {
                setDisplayValue(to);
            }
        };

        animationFrame.current = requestAnimationFrame(animate);

        return () => cancelAnimationFrame(animationFrame.current);
    }, [value, duration]);

    return <span className={`tabular-nums ${className ?? ""}`}>{formatCurrency(displayValue)}</span>;
};

export default AnimatedNumber;